'use client';

import { useState, useRef, useEffect } from 'react';
import { Bell, CheckCheck, Settings } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useNotifications, useBadgeCounts, useMarkAsRead, useMarkAllAsRead } from '../hooks/use-notifications';
import { NotificationItem } from './notification-item';

export function NotificationBell() {
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { data: badgeData } = useBadgeCounts();
  const { data: notificationsData } = useNotifications({ limit: '8' });
  const markAsRead = useMarkAsRead();
  const markAllAsRead = useMarkAllAsRead();

  const unreadCount = badgeData?.unreadTotal || 0;
  const notifications = notificationsData?.data || [];

  // Ce-ai VAZUT in dropdown se marcheaza citit la INCHIDERE (tiparul standard al
  // clopoteilor) — altfel notificarile vechi ramaneau "necitite" la nesfarsit
  // daca nu dadeai click pe fiecare (feedback user). Punctul albastru ramane
  // vizibil cat timp panoul e deschis; la urmatoarea deschidere sunt citite.
  const seenUnreadIds = useRef<string[]>([]);
  useEffect(() => {
    if (isOpen) {
      seenUnreadIds.current = notifications.filter((n) => !n.isRead).map((n) => n.id);
    } else if (seenUnreadIds.current.length > 0) {
      for (const id of seenUnreadIds.current) markAsRead.mutate(id);
      seenUnreadIds.current = [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, notifications]);

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on Escape
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') setIsOpen(false);
    }
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative flex h-10 w-10 items-center justify-center rounded-lg text-neutral-500 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800 dark:bg-neutral-950 hover:text-neutral-700 dark:hover:text-neutral-300 dark:text-neutral-300 transition-colors"
        aria-label="Notificari"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute right-1 top-1 flex h-4.5 min-w-4.5 items-center justify-center rounded-full bg-danger px-1 text-[0.625rem] font-bold text-white animate-pulse">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-96 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 shadow-xl dark:shadow-neutral-900/50 z-50 animate-scale-in origin-top-right overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-neutral-100 dark:border-neutral-800 px-4 py-3">
            <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Notificari</h3>
            {unreadCount > 0 && (
              <button
                onClick={() => markAllAsRead.mutate()}
                className="flex items-center gap-1 text-xs font-medium text-primary hover:text-primary-700 dark:text-primary-300 transition-colors"
              >
                <CheckCheck className="h-3.5 w-3.5" />
                Citeste toate
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-96 overflow-y-auto divide-y divide-neutral-100">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-neutral-400">
                <Bell className="h-8 w-8 mb-2 opacity-50" />
                <p className="text-sm">Nicio notificare</p>
              </div>
            ) : (
              notifications.map((notif) => (
                <NotificationItem
                  key={notif.id}
                  notification={notif}
                  onRead={(id) => markAsRead.mutate(id)}
                  compact
                />
              ))
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-neutral-100 dark:border-neutral-800 p-2 flex items-center gap-2">
            {notifications.length > 0 && (
              <button
                onClick={() => { setIsOpen(false); router.push('/notificari'); }}
                className="flex-1 rounded-lg py-2 text-center text-sm font-medium text-primary hover:bg-primary/5 transition-colors"
              >
                Vezi toate
              </button>
            )}
            <button
              onClick={() => { setIsOpen(false); router.push('/setari?tab=notifications'); }}
              className="flex items-center justify-center gap-1.5 rounded-lg py-2 px-3 text-sm text-neutral-500 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800 hover:text-neutral-700 dark:hover:text-neutral-300 transition-colors"
            >
              <Settings className="h-3.5 w-3.5" />
              <span>Setari</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
